import React, { useState } from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { latestAnalysis } from "@/components/application/finalUnderwritingDisplay";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";
import { handleApiError } from "@/services/api";
import type { BorrowerDetail } from "@/types/borrower";

interface BankVerificationBannerProps {
  borrower: BorrowerDetail;
  /** Admin/underwriter only; everyone else sees the banner without the action. */
  canConfirm: boolean;
}

/**
 * Blocks a deal whose bank came from a merchant voided-check upload until staff
 * confirm the account. Confirming moves it to final underwriting off the AI
 * Analysis data, the same action as Force to Final Underwriting.
 */
const BankVerificationBanner: React.FC<BankVerificationBannerProps> = ({
  borrower,
  canConfirm,
}) => {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (borrower.app_status !== "bank_verification_required") return null;

  const hasStatementsAnalysis = latestAnalysis(borrower, "statements") != null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await borrowerService.forceFinalUnderwriting(borrower.id);
      queryClient.setQueryData(queryKeys.borrower(borrower.id), updated);
      void queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrower.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.borrowers() });
    } catch (e) {
      setError(e instanceof AxiosError ? handleApiError(e) : "Could not confirm verification.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-amber-400/40 bg-amber-50 p-4 shadow-none dark:bg-amber-950/20">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">Bank verification required</h2>
          <p className="text-sm text-muted-foreground">
            {hasStatementsAnalysis
              ? "The merchant uploaded a voided check. Perform a manual login with merchant to verify bank account."
              : "The merchant uploaded a voided check. An AI Analysis is required before verification can be confirmed."}
          </p>
          {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        </div>
        {canConfirm && (
          <Button
            type="button"
            size="sm"
            className="bg-amber-600 text-white hover:bg-amber-600/90 active:bg-amber-600/80"
            disabled={busy || !hasStatementsAnalysis}
            onClick={() => void confirm()}
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Manual Login Complete
          </Button>
        )}
      </div>
    </Card>
  );
};

export default BankVerificationBanner;
